import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Scale, Search, ChevronLeft, ChevronRight, Plus, Eye, Edit, Trash2, Building2, Truck, MapPin, Weight, Calendar, Filter, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import useStickyState from '../lib/useStickyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScaleReceiptLogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useStickyState('scalelog:search', '');
  const [generatorFilter, setGeneratorFilter] = useStickyState('scalelog:generatorFilter', 'all');
  const [truckFilter, setTruckFilter] = useStickyState('scalelog:truckFilter', 'all');
  const [templateId, setTemplateId] = useState(null);
  const [activeTab, setActiveTab] = useStickyState('scalelog:activeTab', 'entries');
  const [dateFilter, setDateFilter] = useStickyState('scalelog:dateFilter', 'all');
  const [customDateStart, setCustomDateStart] = useStickyState('scalelog:customDateStart', '');
  const [customDateEnd, setCustomDateEnd] = useStickyState('scalelog:customDateEnd', '');
  const [selectedMonth, setSelectedMonth] = useStickyState('scalelog:selectedMonth', '');
  const [selectedYear, setSelectedYear] = useStickyState('scalelog:selectedYear', '');

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const clearDateFilter = () => {
    setDateFilter('all');
    setCustomDateStart('');
    setCustomDateEnd('');
    setSelectedMonth('');
    setSelectedYear('');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch summary
      const summaryRes = await axios.get(`${API}/scale-receipt/summary`, { headers });
      setSummary(summaryRes.data);
      setEntries(summaryRes.data.entries || []);

      // Get template ID for the "Add Entry" button
      const templatesRes = await axios.get(`${API}/forms/templates`, { headers });
      const scaleTemplate = templatesRes.data.find(t => t.name === 'Scale Receipt');
      if (scaleTemplate) {
        setTemplateId(scaleTemplate.id);
      }
    } catch (err) {
      console.error('Failed to fetch scale receipt data:', err);
      toast.error('Failed to load scale receipts');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle YYYY-MM-DD format to avoid timezone issues
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  // Get unique generators and trucks for filters
  const generators = summary ? Object.keys(summary.by_generator) : [];
  const trucks = summary ? Object.keys(summary.by_truck) : [];

  // Filter entries by search, generator, truck, and date
  const filteredEntries = entries.filter(entry => {
    // Generator filter
    if (generatorFilter !== 'all' && entry.generator_name !== generatorFilter) return false;
    // Truck filter
    if (truckFilter !== 'all' && entry.truck_unit !== truckFilter) return false;
    
    // Text search filter
    if (search) {
      const s = search.toLowerCase();
      const matchesSearch = (
        (entry.generator_name || '').toLowerCase().includes(s) ||
        (entry.source_lsd || '').toLowerCase().includes(s) ||
        (entry.truck_unit || '').toLowerCase().includes(s) ||
        (entry.facility_name || '').toLowerCase().includes(s) ||
        (entry.bol_number || '').toLowerCase().includes(s)
      );
      if (!matchesSearch) return false;
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const entryDate = new Date(entry.date || entry.submitted_at);
      if (isNaN(entryDate.getTime())) return true;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        const entryDay = new Date(entryDate);
        entryDay.setHours(0, 0, 0, 0);
        return entryDay.getTime() === today.getTime();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return entryDate >= weekAgo;
      } else if (dateFilter === 'month') {
        if (selectedMonth && selectedYear) {
          const year = parseInt(selectedYear);
          const month = parseInt(selectedMonth) - 1;
          return entryDate.getFullYear() === year && entryDate.getMonth() === month;
        }
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return entryDate >= monthAgo;
      } else if (dateFilter === 'year') {
        if (selectedYear) {
          return entryDate.getFullYear() === parseInt(selectedYear);
        }
        const yearAgo = new Date(today);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        return entryDate >= yearAgo;
      } else if (dateFilter === 'custom') {
        if (customDateStart && customDateEnd) {
          const start = new Date(customDateStart);
          const end = new Date(customDateEnd);
          end.setHours(23, 59, 59, 999);
          return entryDate >= start && entryDate <= end;
        } else if (customDateStart) {
          return entryDate >= new Date(customDateStart);
        } else if (customDateEnd) {
          const end = new Date(customDateEnd);
          end.setHours(23, 59, 59, 999);
          return entryDate <= end;
        }
      }
    }
    
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/forms">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Forms
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Scale className="h-6 w-6 text-blue-500" />
              Scale Receipt Log
            </h1>
            <p className="text-muted-foreground text-sm">
              Track manifested scale receipts by generator, truck, and location
            </p>
          </div>
        </div>
        {templateId && (
          <Link to={`/dashboard/forms/fill/${templateId}`}>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Receipt
            </Button>
          </Link>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-blue-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Receipts</div>
              <div className="text-2xl font-bold text-foreground">{summary.total_entries}</div>
            </CardContent>
          </Card>
          <Card className="border-green-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Net Weight</div>
              <div className="text-2xl font-bold text-green-400">
                {summary.total_net_tonnes.toLocaleString()} tonnes
              </div>
            </CardContent>
          </Card>
          <Card className="border-purple-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Generators</div>
              <div className="text-2xl font-bold text-purple-400">
                {Object.keys(summary.by_generator).length}
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Trucks Used</div>
              <div className="text-2xl font-bold text-orange-400">
                {Object.keys(summary.by_truck).length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="entries">All Entries</TabsTrigger>
          <TabsTrigger value="by-date">By Date</TabsTrigger>
          <TabsTrigger value="by-generator">By Generator</TabsTrigger>
          <TabsTrigger value="by-truck">By Truck</TabsTrigger>
          <TabsTrigger value="by-location">By Location</TabsTrigger>
        </TabsList>

        {/* Entries Tab */}
        <TabsContent value="entries" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by generator, LSD, truck..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={generatorFilter} onValueChange={setGeneratorFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Generators" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Generators</SelectItem>
                {generators.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={truckFilter} onValueChange={setTruckFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Trucks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Trucks</SelectItem>
                {trucks.map(t => (
                  <SelectItem key={t} value={t}>Unit {t}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Date filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">By Month</SelectItem>
                  <SelectItem value="year">By Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateFilter === 'month' && (
              <>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, idx) => (
                      <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-[90px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            {dateFilter === 'year' && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[90px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {dateFilter === 'custom' && (
              <>
                <Input
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="w-[140px]"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="w-[140px]"
                />
              </>
            )}

            {dateFilter !== 'all' && (
              <Button variant="ghost" size="sm" onClick={clearDateFilter}>
                <X className="h-4 w-4" />
              </Button>
            )}

            <div className="text-sm text-muted-foreground ml-auto">
              {filteredEntries.length} entries
            </div>
          </div>

          {/* Entries Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Generator</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Source LSD</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Truck</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Facility</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Gross</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Tare</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Net</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-muted-foreground">
                          Loading...
                        </td>
                      </tr>
                    ) : filteredEntries.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-muted-foreground">
                          No scale receipts found
                        </td>
                      </tr>
                    ) : (
                      filteredEntries.map((entry) => (
                        <tr key={entry.id} className="border-b border-border/50 hover:bg-secondary/30">
                          <td className="p-4 text-sm">{formatDate(entry.date)}</td>
                          <td className="p-4 text-sm font-medium">{entry.generator_name}</td>
                          <td className="p-4 text-sm text-muted-foreground">{entry.source_lsd}</td>
                          <td className="p-4 text-sm">{entry.truck_unit}</td>
                          <td className="p-4 text-sm text-muted-foreground">{entry.facility_name}</td>
                          <td className="p-4 text-sm text-right">{entry.gross_weight?.toFixed(2)}</td>
                          <td className="p-4 text-sm text-right text-muted-foreground">{entry.tare_weight?.toFixed(2)}</td>
                          <td className="p-4 text-sm text-right font-medium text-green-400">{entry.net_weight?.toFixed(2)}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Link to={`/dashboard/forms/view/${entry.id}`}>
                                <Button variant="ghost" size="sm" title="View">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </Link>
                              <Link to={`/dashboard/forms/edit/${entry.id}`}>
                                <Button variant="ghost" size="sm" title="Edit" data-testid={`scale-edit-${entry.id}`}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </Link>
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={async () => {
                                    if (!window.confirm('Delete this scale receipt permanently?')) return;
                                    try {
                                      const token = localStorage.getItem('vaultai_token');
                                      await axios.delete(`${API}/forms/submissions/${entry.id}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success('Scale receipt deleted');
                                      fetchData();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to delete');
                                    }
                                  }}
                                  data-testid={`scale-delete-${entry.id}`}
                                  title="Delete entry"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Date Tab */}
        <TabsContent value="by-date" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-5 w-5 text-cyan-400" />
                Daily Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary && summary.by_date && (
                <div className="space-y-3">
                  {Object.entries(summary.by_date).map(([date, data]) => (
                    <div key={date} className="p-4 rounded-lg bg-secondary/30 border border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-lg font-medium">
                            {formatDate(date)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {data.loads} load{data.loads !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-400">{data.net_tonnes.toLocaleString()} t net</div>
                          <div className="text-xs text-muted-foreground">Gross: {data.gross_tonnes.toLocaleString()} t</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <Truck className="h-3 w-3" /> Trucks
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {data.trucks.map(truck => (
                              <span key={truck} className="bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded text-xs">
                                Unit {truck}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <Building2 className="h-3 w-3" /> Generators
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {data.generators.map(gen => (
                              <span key={gen} className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs">
                                {gen}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {Object.keys(summary.by_date).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No scale receipts found
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Generator Tab */}
        <TabsContent value="by-generator" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-5 w-5 text-purple-400" />
                Tonnage by Generator (Client)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary && (
                <div className="space-y-3">
                  {Object.entries(summary.by_generator).map(([name, data]) => (
                    <div key={name} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50">
                      <div>
                        <div className="font-medium text-foreground">{name}</div>
                        <div className="text-xs text-muted-foreground">{data.loads} loads</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-400">{data.net_tonnes.toLocaleString()} t</div>
                        <div className="text-xs text-muted-foreground">Gross: {data.gross_tonnes.toLocaleString()} t</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Truck Tab */}
        <TabsContent value="by-truck" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-5 w-5 text-orange-400" />
                Tonnage by Truck
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {Object.entries(summary.by_truck).map(([unit, data]) => (
                    <div key={unit} className="p-3 rounded-lg bg-secondary/30 border border-border/50">
                      <div className="font-medium text-foreground">Unit {unit}</div>
                      <div className="text-xs text-muted-foreground">{data.loads} loads</div>
                      <div className="text-lg font-bold text-orange-400 mt-2">
                        {data.net_tonnes.toLocaleString()} t
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Location Tab */}
        <TabsContent value="by-location" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By Source LSD */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-400" />
                  By Source Location (LSD)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary && (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {Object.entries(summary.by_source_lsd).map(([lsd, data]) => (
                      <div key={lsd} className="flex items-center justify-between p-2 rounded bg-secondary/20">
                        <div>
                          <div className="font-mono text-sm text-foreground">{lsd}</div>
                          <div className="text-xs text-muted-foreground">{data.generator}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-blue-400">{data.net_tonnes.toLocaleString()} t</div>
                          <div className="text-xs text-muted-foreground">{data.loads} loads</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* By Facility */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Weight className="h-5 w-5 text-green-400" />
                  By Disposal Facility
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary && (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {Object.entries(summary.by_facility).map(([facility, data]) => (
                      <div key={facility} className="flex items-center justify-between p-2 rounded bg-secondary/20">
                        <div>
                          <div className="font-medium text-foreground">{facility}</div>
                          <div className="text-xs text-muted-foreground">{data.loads} loads</div>
                        </div>
                        <div className="font-medium text-green-400">{data.net_tonnes.toLocaleString()} t</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
